import { TFunction } from 'i18next'
import { z } from 'zod'

import { AmenityListItem } from '@/api/reservations/get-amenities'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { getAssetLink } from '@/helpers/get-asset-link'
import { theme } from '@/theme/theme'

export const AMENITIES_TABLE_ID = 'reservations-amenities-table'

export const amenitiesTableFiltersSchema = z.object({
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
})

export type AmenitiesTableFilters = z.infer<typeof amenitiesTableFiltersSchema>

export const amenitiesTableInitialFilters: AmenitiesTableFilters = {
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
}

const getAmenityPriceCell = (t: TFunction<['reservations']>, amenity: AmenityListItem) => {
  const slots = amenity.multiDayBooking ? amenity.multidaySlots : amenity.hourlySlots
  const prices = (slots ?? []).map((slot) => Number(slot.price) || 0)

  if (prices.length === 0) {
    return (
      <Badge color="green" size="small" variant="light">
        {t('reservations:amenities.price.free')}
      </Badge>
    )
  }

  const uniquePrices = [...new Set(prices)]
  const minPrice = Math.min(...uniquePrices)
  const maxPrice = Math.max(...uniquePrices)

  if (maxPrice === 0) {
    return (
      <Badge color="green" size="small" variant="light">
        {t('reservations:amenities.price.free')}
      </Badge>
    )
  }

  if (uniquePrices.length === 1) {
    return (
      <Typography color="black800" size="text-small" weight="regular">
        ${uniquePrices[0]}
      </Typography>
    )
  }

  return (
    <Typography color="black800" size="text-small" weight="regular">
      ${minPrice}–${maxPrice}
    </Typography>
  )
}

export const getAmenitiesTableColumns = (
  t: TFunction<['reservations']>,
): TableColumn<AmenityListItem>[] => [
  {
    id: 'avatar',
    render: (amenity) => (
      <Avatar
        alt={amenity.name}
        shape="square"
        size="small"
        src={getAssetLink({
          fallback: { file: 'default_amenity.png' },
          file: amenity.image ?? undefined,
          path: 'chitown-building',
        })}
        style={{ height: theme.space[56], width: theme.space[56] }}
      />
    ),
    title: '',
    width: '7.6rem',
  },
  {
    breakLines: 2,
    id: 'name',
    render: (amenity) => amenity.name,
    title: t('reservations:amenities.columns.name'),
    width: '14rem',
  },
  {
    breakLines: 2,
    id: 'description',
    render: (amenity) => amenity.description || EMPTY_VALUE,
    title: t('reservations:amenities.columns.description'),
    width: '24rem',
  },
  {
    id: 'type',
    render: (amenity) => (
      <Typography color="black800" size="text-small" weight="regular">
        {amenity.multiDayBooking
          ? t('reservations:amenities.type.multiday')
          : t('reservations:amenities.type.hourly')}
      </Typography>
    ),
    title: t('reservations:amenities.columns.type'),
    width: '10rem',
  },
  {
    id: 'spaces',
    render: (amenity) => (
      <Typography color="black800" size="text-small" weight="regular">
        {amenity.locations.length || EMPTY_VALUE}
      </Typography>
    ),
    title: t('reservations:amenities.columns.spaces'),
    width: '8rem',
  },
  {
    id: 'availableForBooking',
    render: (amenity) => (
      <Typography color="black800" size="text-small" weight="regular">
        {amenity.hidden
          ? t('reservations:amenities.availableForBooking.no')
          : t('reservations:amenities.availableForBooking.yes')}
      </Typography>
    ),
    title: t('reservations:amenities.columns.availableForBooking'),
    width: '13rem',
  },
  {
    id: 'price',
    render: (amenity) => getAmenityPriceCell(t, amenity),
    title: t('reservations:amenities.columns.price'),
    width: '10rem',
  },
]
